/**
 * Responsibility: the one row of controls every shelf page carries.
 *
 * **It used to carry the section tabs as well, and no longer does.** Which
 * part of a collection you are looking at and how it is drawn are different
 * questions, and the tabs now live where every page's tabs live - the card's
 * own first row, via `PageContainer`'s `tabs` slot. That put the strip flush
 * at the top instead of under a body's worth of padding, and left this row
 * to the controls, which on the search page were being squeezed into
 * whatever the tabs did not want.
 *
 * So what is left is the page's own controls, then how-to-draw-it on the
 * right: grid or table, and expand.
 *
 * **Expand** hides everything above the list - the figures, the filters - and
 * gives the whole container to the cards. It is a view of the same data rather
 * than a different page, so it is a toggle here rather than a route.
 *
 * **The drawing controls are optional.** A page with nothing to draw as a
 * grid passes no `view` and gets its own controls and nothing it cannot use,
 * rather than a second toolbar that looks the same and drifts.
 */
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import CollapseIcon from '@mui/icons-material/CloseFullscreen';
import ExpandIcon from '@mui/icons-material/OpenInFull';
import GridIcon from '@mui/icons-material/GridView';
import TableIcon from '@mui/icons-material/TableRows';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { ReactNode } from 'react';

/** How the cards are drawn. Independent of which section is open. */
export type ShelfView = 'grid' | 'table';

type ShelfToolbarProps = {
  /** Omit on a page with nothing to draw as a grid - Settings, say. */
  view?: ShelfView;
  onView?: (next: ShelfView) => void;
  /** Omit where there is nothing above the content worth hiding. */
  expanded?: boolean;
  onExpanded?: (next: boolean) => void;
  /** The page's own controls, on the left of the drawing buttons. */
  children?: ReactNode;
};

export function ShelfToolbar({
  view,
  onView,
  expanded,
  onExpanded,
  children,
}: ShelfToolbarProps) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ flexShrink: 0, minWidth: 0 }}
    >
      {/* **Grows, and ends at the right edge.** `ml: auto` pushed a
          fixed-width group over and left whatever was spare as a gap
          between the last control and the edge. Letting the group grow
          and justifying it to the end means a control that wants the
          slack - the search box - takes it, and a page of small buttons
          still sits hard right exactly as before. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ flexGrow: 1, minWidth: 0, justifyContent: { md: 'flex-end' } }}
      >
        {children}

        {/* Square, unlabelled and together, because these three are one
            group: how the thing on screen is drawn. */}
        {view !== undefined && onView !== undefined ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_event, next: ShelfView | null) => next && onView(next)}
          >
            <ToggleButton value="grid" aria-label="Grid">
              <Tooltip title="Grid">
                <GridIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table">
              <Tooltip title="Table">
                <TableIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}

        {expanded !== undefined && onExpanded !== undefined ? (
          <ToggleButtonGroup exclusive size="small" value={expanded ? 'on' : 'off'}>
            <ToggleButton
              value={expanded ? 'off' : 'on'}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              onClick={() => onExpanded(!expanded)}
            >
              <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
      </Stack>
    </Stack>
  );
}
